import { useCallback, useEffect, useRef, useState } from 'react'

import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import { LinkedPageKind } from '@ebalance/proto/bffuser/v1/workflow_template_model_pb'
import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form'
import { Controller, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  FieldRoot,
  HStack,
  Input,
  FieldErrorText as RawFieldErrorText,
  SecondaryButton,
  Text,
  VStack,
  chakra,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronUpRegular,
  FaPlusRegular,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { DsSelect } from '~/components/common/DsSelect'
import { enumSetValueAs } from '~/components/common/EnumSelect'
import { MultiSelect } from '~/components/common/MultiSelect'
import type { MultiSelectOption } from '~/components/common/MultiSelect'
import {
  isWorkflowAutomationAvailable,
  useWorkflowAutomationAvailability,
} from '~/hooks/useWorkflowAutomationAvailability'
import { truncateToCodePointLength } from '~/libs/codePoint'
import { StepIconButton } from '~/pages/AdminWorkflowTemplates/components/StepIconButton'
import { SubStepApprovalSettingField } from '~/pages/AdminWorkflowTemplates/components/SubStepApprovalSettingField'
import { SubStepPlanAutoSubmitOffsetField } from '~/pages/AdminWorkflowTemplates/components/SubStepPlanAutoSubmitOffsetField'
import { SubStepPlanCreationTimeField } from '~/pages/AdminWorkflowTemplates/components/SubStepPlanCreationTimeField'
import {
  approvalBadgeColors,
  approvalBadgeLabel,
  resolveApprovalBadgeSummary,
} from '~/pages/AdminWorkflowTemplates/models/approvalBadge'
import type { ApprovalProcesses } from '~/pages/AdminWorkflowTemplates/models/approvalBadge'
import {
  DEFAULT_APPROVAL_SETTING,
  allowedApprovalModes,
  resolveApprovalSetting,
} from '~/pages/AdminWorkflowTemplates/models/approvalSetting'
import { toggleAssigneeSelection } from '~/pages/AdminWorkflowTemplates/models/assigneeSelection'
import { buildLinkedPageKindOptions } from '~/pages/AdminWorkflowTemplates/models/linkedPageOptions'
import type { WorkflowTemplateFormValues } from '~/pages/AdminWorkflowTemplates/models/schema'
import { routePaths } from '~/routes/routePaths'

/**
 * 「指定なし」 chip を表す sentinel 値 (spec AC-WFT-010d)。 実 member_id
 * (eauth member ID) と衝突しない専用文字列。 RHF の実 field 値
 * (`assigneeMemberIds`) には決して含めない — 選択肢 UI 上にのみ存在する
 * ラベルで、 backend / DB は「指定なし」 を持たず空配列で表現する
 * (Naming convention 「指定なし」 semantics)。
 */
const UNASSIGNED_SENTINEL = '__unassigned__'

/**
 * chakra v3 の `FieldErrorTextProps` は本プロジェクトの型解決で `HTMLChakraProps` が
 * collapse し `children` が欠落する (= ecloud 0.14 / chakra 3.29 の d.ts 解決問題)。
 * v3 本来の prop シグネチャ (`HTMLChakraProps<'div'>` = children + style props) へ
 * cast して復元する。 ランタイムは ecloud の `FieldErrorText` そのまま。
 */
const FieldErrorText = RawFieldErrorText as React.FC<HTMLChakraProps<'div'>>

type AssigneeMultiSelectProps = {
  value: string[] | undefined
  onChange: (value: string[]) => void
  options: MultiSelectOption[]
  placeholder: string
  ariaLabel: string
}

/**
 * サブステップ担当者 multi-select。
 *
 * UI 上では「指定なし」 sentinel を選択状態として表示するが、RHF 値
 * (`assigneeMemberIds`) からは常に除外して空配列として保存する。
 * この分離により、未設定時は placeholder、 「指定なし」 選択時は chip を
 * 表示する prototype 挙動を再現する。
 */
const AssigneeMultiSelect = ({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel,
}: AssigneeMultiSelectProps): JSX.Element => {
  const [displaySelected, setDisplaySelected] = useState<string[]>(
    () => value ?? [],
  )
  const lastEmittedValueRef = useRef<string[]>(value ?? [])

  useEffect(() => {
    const nextValue = value ?? []
    if (
      JSON.stringify(nextValue) !== JSON.stringify(lastEmittedValueRef.current)
    ) {
      setDisplaySelected(nextValue)
      lastEmittedValueRef.current = nextValue
    }
  }, [value])

  const handleChange = useCallback(
    (next: string[]): void => {
      const resolved = toggleAssigneeSelection(
        displaySelected,
        next,
        UNASSIGNED_SENTINEL,
      )
      const filtered = resolved.filter((v) => v !== UNASSIGNED_SENTINEL)
      setDisplaySelected(resolved)
      lastEmittedValueRef.current = filtered
      onChange(filtered)
    },
    [displaySelected, onChange],
  )

  return (
    <MultiSelect
      aria-label={ariaLabel}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      selected={displaySelected}
    />
  )
}

type Props = {
  bigStepIndex: number
  control: Control<WorkflowTemplateFormValues>
  errors: FieldErrors<WorkflowTemplateFormValues>
  /** リンク先の変更で承認フロー設定を書き換える (Edit.tsx の useForm から渡す) */
  getValues: UseFormGetValues<WorkflowTemplateFormValues>
  setValue: UseFormSetValue<WorkflowTemplateFormValues>
  /** 担当者一覧 (Edit.tsx で useGetWorkflowTemplateFormOptions を呼び、 ここに渡す) */
  members: Member[]
  /**
   * 承認フローバッジ算出用の全プロセス設定 (Edit.tsx で
   * useGetWorkflowTemplateFormOptions を呼び、 ここに渡す)。 未取得時は undefined
   * (= graceful degrade、 バッジ非表示)。
   */
  approvalProcesses?: ApprovalProcesses
  /**
   * 親大ステップの現在のタイトル (BigStepEditor が watch して渡す)。
   * spec AC-010c の推奨マッピングを引く key。 spec P0 6 種
   * (需要想定 / 供給力確認 / スポット入札 / 時間前入札 / 計画提出 / 同時同量監視)
   * に一致する場合のみ Select 先頭に「推奨」 optgroup を挿入する。
   * それ以外 / undefined / 「その他」 / 写像未定義プリセット外 では非表示。
   */
  bigStepTitle?: string
}

/**
 * サブステップエディタ (design `.ct-substep-editor` 準拠)。
 *
 * row1: 番号 | 名称(flex:1) | リンク先 | 承認フロー | 担当者 | 上下/削除
 * row2: 説明 (インデント 32px・全幅・100文字)
 *
 * 承認フロー: スポット入札 / 時間前入札 は approval-flow 画面の設定を読むバッジ
 * (AC-WFT-010a。 クリックで adminApprovalFlow 画面へ)、 それ以外は雛形が持つ設定の
 * 編集欄 (AC-WFT-010e / 010f)。
 */
export const SubStepEditor = ({
  bigStepIndex,
  control,
  errors,
  getValues,
  setValue,
  members,
  approvalProcesses,
  bigStepTitle,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const automationAvailable = isWorkflowAutomationAvailable(
    useWorkflowAutomationAvailability(),
  )
  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: `bigSteps.${bigStepIndex}.subSteps`,
  })

  const subSteps = useWatch({
    control,
    name: `bigSteps.${bigStepIndex}.subSteps`,
  })

  const bigStepErrors = errors.bigSteps?.[bigStepIndex]?.subSteps

  const handleAddSubStep = (): void => {
    append({
      displayOrder: fields.length + 1,
      name: '',
      description: undefined,
      linkedPageKind: undefined,
      assigneeMemberIds: [],
      approvalSetting: DEFAULT_APPROVAL_SETTING,
    })
  }

  // 担当者 multi-select の選択肢 (先頭に「指定なし」 chip、AC-WFT-010d)。
  const assigneeOptions = [
    {
      value: UNASSIGNED_SENTINEL,
      label: t('page:adminWorkflowTemplates.subStep.assigneeNone'),
    },
    ...members.map((m) => ({ value: m.memberId, label: m.displayName })),
  ]

  return (
    <VStack align="stretch" gap={2}>
      {fields.map((field, subIndex) => {
        const subErrors = errors.bigSteps?.[bigStepIndex]?.subSteps?.[subIndex]
        const approvalErrorId = `subStep-approval-error-${bigStepIndex}-${subIndex}`

        return (
          // ct-substep-editor (surface-secondary / radius-base)
          <VStack
            key={field.id}
            align="stretch"
            gap={2}
            padding={2}
            background="Surface.secondary"
            borderRadius="base"
          >
            {/* row1: 番号 | 名称 | リンク先 | 承認バッジ | 担当者 | 上下/削除

                揃えは design `.ct-substep-row` と同じ `align-items:center`。
                **名称欄のエラー文言だけを行の外 (row1 の下) に出す** のがポイントで、
                これが 2026-08-18 デザイナー指摘「エラーメッセージを表示する際でも、
                input form の表示位置は揃える」への対応 (下の row1.5 を参照)。

                以前は「上揃え + 静的オフセット」で揃えていたが、 担当者 MultiSelect は
                chip が折り返すと 40px を超えて伸びるため、 固定値では追従できなかった
                (PR #5811 codex-review 指摘)。 center のままにして、 行の高さを変える
                原因の方を行外へ出す形にした。 */}
            <HStack align="center" gap={2}>
              {/* 番号バッジ (high-emphasis / 14px / weight 700 / min-w 24px) */}
              {/* eslint-disable-next-line ebalance/no-adhoc-typography -- design
                  .ct-substep-num-badge (case-templates.html:73) = md/700 と一致。
                  数値バッジのため role 対象外 */}
              <Text
                flexShrink={0}
                minWidth="24px"
                color="Text.highEmphasis"
                fontSize="md"
                fontWeight="bold"
                fontVariantNumeric="tabular-nums"
              >
                {bigStepIndex + 1}.{subIndex + 1}
              </Text>

              {/* 名称 (design .sub-title: flex:1 min-width:0 で左右一杯に伸ばす) */}
              <FieldRoot
                alignItems="stretch"
                flex="1 1 0"
                minWidth={0}
                invalid={subErrors?.name !== undefined}
              >
                <Controller
                  control={control}
                  name={`bigSteps.${bigStepIndex}.subSteps.${subIndex}.name`}
                  render={({ field: f }) => (
                    <Input
                      {...f}
                      width="100%"
                      aria-describedby={
                        subErrors?.name !== undefined
                          ? `subStep-name-error-${bigStepIndex}-${subIndex}`
                          : undefined
                      }
                      aria-label={t('page:adminWorkflowTemplates.subStep.name')}
                      id={`subStep-name-${bigStepIndex}-${subIndex}`}
                      onChange={(e) => {
                        f.onChange(
                          truncateToCodePointLength(e.target.value, 30),
                        )
                      }}
                      placeholder={t(
                        'page:adminWorkflowTemplates.subStep.namePlaceholder',
                      )}
                      size="md"
                    />
                  )}
                />
                {/* エラー文言はここに置かない (row1 の外へ出す)。 入力欄の直下に
                    置くと行の高さが変わり、 他の項目が再センタリングされて動く。
                    a11y の関連付けは Input の aria-describedby で明示的に張る。 */}
              </FieldRoot>

              {/* リンク先画面 + 承認フロー */}
              <Controller
                control={control}
                name={`bigSteps.${bigStepIndex}.subSteps.${subIndex}.linkedPageKind`}
                render={({ field: f }) => {
                  const currentKind = f.value as LinkedPageKind | undefined
                  const approvalModes = allowedApprovalModes(currentKind)
                  const summary =
                    approvalModes.length === 0
                      ? resolveApprovalBadgeSummary(
                          currentKind,
                          approvalProcesses,
                        )
                      : undefined
                  const badgeLabel = approvalBadgeLabel(summary, t)
                  const badgeColors = approvalBadgeColors(summary)

                  // 選択肢一式 (推奨 / 通常カテゴリ / deprecated fallback) の解決は
                  // linkedPageOptions model に集約。 SubStepEditor は結果を
                  // <optgroup> / <option> へ描くだけ。 deprecatedOption は現在値が
                  // 選択肢に無い場合 (= deprecated PLAN_SUBMISSION = 15 を持つ overlay
                  // tenant 等) の disabled fallback option 情報 (ADR 「保存で自動修復」
                  // 経路の可視化。 disabled により再選択は不可、 別 option 選択で
                  // state 更新可能、 無操作保存で 15 が round-trip 維持 = overlay-compat)。
                  const { recommended, groups, deprecatedOption } =
                    buildLinkedPageKindOptions(bigStepTitle, currentKind)
                  return (
                    <HStack align="center" gap={2}>
                      {/* リンク先 Select は **DS `Select`** (`DsSelect`) を使う。
                          native `<select>` は OS 既定の見た目・caret になり DS と
                          揃わない (2026-08-19 デザイナー指摘「DS 準拠していない
                          select」)。 `DsSelect` はグループ (= `<optgroup>` 相当)・
                          選べない選択肢・選択肢に無い値の fallback ラベルを持つので、
                          この select が必要とする表現をすべて満たせる。

                          幅は **固定 200px**。
                          [design 差異] design `.sub-ref` は `min-width:140px;
                          max-width:200px` だが、 `minWidth` + `maxWidth` だけだと
                          **選択前後で幅が変わり** (未選択 140px → 長い選択肢 200px)、
                          行内の他の要素が左右に動く (2026-08-19 デザイナー指摘:
                          全画面共通)。 名称欄は最大 60px
                          狭くなるが、 選択のたびに行が揺れない方を採る。 */}
                      <FieldRoot
                        width="auto"
                        invalid={subErrors?.linkedPageKind !== undefined}
                      >
                        <DsSelect
                          width="200px"
                          onBlur={f.onBlur}
                          onChange={(v) => {
                            const nextKind = enumSetValueAs(v) as
                              | LinkedPageKind
                              | undefined
                            f.onChange(nextKind)
                            const approvalPath =
                              `bigSteps.${bigStepIndex}.subSteps.${subIndex}.approvalSetting` as const
                            const nextSetting = resolveApprovalSetting(
                              nextKind,
                              getValues(approvalPath),
                              automationAvailable,
                            )
                            setValue(approvalPath, nextSetting, {
                              shouldValidate: true,
                              shouldDirty: true,
                            })
                          }}
                          options={[
                            // 「リンクなし」 は **placeholder ではなく選択肢**として
                            // 持つ。 placeholder だけにすると、 一度リンク先を選んだ後に
                            // **リンク無しへ戻す操作が UI から消える** (DS Select は
                            // clear ボタンを出さない)。 native `<select>` 時代に
                            // `value=""` の option を選び直せば戻せていた挙動を維持する。
                            //
                            // 値は `UNSPECIFIED` (= 0)。 schema の JSDoc どおり 0 は
                            // 「リンクなし」 で undefined と等価。 **`undefined` は使えない**:
                            // React Hook Form は値が undefined の field を読む時に
                            // defaultValue へフォールバックするため、 undefined を書いても
                            // 直前の選択値に戻ってしまう (2026-08-20 実測)。 0 は「定義された
                            // 値」なので保持され、 送信時に Edit.tsx が undefined へ戻す。
                            {
                              value: String(LinkedPageKind.UNSPECIFIED),
                              label: t(
                                'page:adminWorkflowTemplates.subStep.linkedPageNone',
                              ),
                            },
                            ...(deprecatedOption !== undefined
                              ? [
                                  {
                                    value: String(deprecatedOption.kind),
                                    label: `${t(deprecatedOption.labelKey)}${t('page:adminWorkflowTemplates.subStep.linkedPageDeprecatedSuffix')}`,
                                    disabled: true,
                                  },
                                ]
                              : []),
                            ...(recommended.length > 0
                              ? [
                                  {
                                    label: t(
                                      'page:adminWorkflowTemplates.subStep.recommendedGroupLabel',
                                    ),
                                    options: recommended.map((item) => ({
                                      value: String(item.kind),
                                      label: t(item.routeNameKey),
                                    })),
                                  },
                                ]
                              : []),
                            ...groups.map((group) => ({
                              label: t(group.groupLabelKey),
                              options: group.items.map((item) => ({
                                value: String(item.kind),
                                label: t(item.routeNameKey),
                              })),
                            })),
                          ]}
                          placeholder={t(
                            'page:adminWorkflowTemplates.subStep.linkedPageNone',
                          )}
                          value={f.value}
                        />
                      </FieldRoot>

                      {/* 承認フローバッジ (#3): 承認対象 kind のときのみ表示。
                          承認フロー設定画面は別タブで開く。 2026-10-16 のデザイナー決定
                          (別途指定の無いリンクは別タブで開かない) の下での別タブ指定で、
                          理由は編集中の内容を失わないため (この編集画面に離脱確認が無い)。
                          2026-10-16 Des レーン決定。 内部遷移なので external icon は付けない。 */}
                      {badgeLabel !== undefined &&
                        badgeColors !== undefined && (
                          <chakra.a
                            flexShrink={0}
                            textDecoration="none"
                            href={routePaths.adminApprovalFlow.path}
                            rel="noopener noreferrer"
                            target="_blank"
                            title={t(
                              'page:adminWorkflowTemplates.subStep.approvalBadgeHint',
                            )}
                          >
                            <chakra.span
                              textStyle="badge"
                              alignItems="center"
                              display="inline-flex"
                              height="24px"
                              color={badgeColors.color}
                              background={badgeColors.background}
                              borderRadius="base"
                              whiteSpace="nowrap"
                              cursor="pointer"
                              paddingX="8px" // space-2
                            >
                              {badgeLabel}
                            </chakra.span>
                          </chakra.a>
                        )}
                      {approvalModes.length > 0 && (
                        <SubStepApprovalSettingField
                          bigStepIndex={bigStepIndex}
                          control={control}
                          errorId={
                            subErrors?.approvalSetting !== undefined
                              ? approvalErrorId
                              : undefined
                          }
                          linkedPageKind={currentKind}
                          members={members}
                          modes={approvalModes}
                          subIndex={subIndex}
                        />
                      )}
                    </HStack>
                  )
                }}
              />

              {/* 担当者 multi-select (spec AC-WFT-010, min-width 180px。 chip
                  収容のため単一 select の 130px から拡大、 Implementation Notes) */}
              <FieldRoot flexShrink={0} minWidth="180px" maxWidth="260px">
                <Controller
                  control={control}
                  name={`bigSteps.${bigStepIndex}.subSteps.${subIndex}.assigneeMemberIds`}
                  render={({ field: f }) => (
                    <AssigneeMultiSelect
                      key={field.id}
                      ariaLabel={t(
                        'page:adminWorkflowTemplates.subStep.assigneeSelectLabel',
                      )}
                      onChange={f.onChange}
                      options={assigneeOptions}
                      placeholder={t(
                        'page:adminWorkflowTemplates.subStep.assigneePlaceholder',
                      )}
                      value={f.value}
                    />
                  )}
                />
              </FieldRoot>

              {/* 上 / 下 / 削除。 40px (入力欄と同じ高さ)。
                  design の 32px から意図的に変えている理由は StepIconButton の JSDoc。 */}
              <StepIconButton
                disabled={subIndex === 0}
                label={t('common:action.moveUp')}
                onClick={() => {
                  swap(subIndex, subIndex - 1)
                }}
              >
                <FaChevronUpRegular boxSize="16px" />
              </StepIconButton>
              <StepIconButton
                disabled={subIndex === fields.length - 1}
                label={t('common:action.moveDown')}
                onClick={() => {
                  swap(subIndex, subIndex + 1)
                }}
              >
                <FaChevronDownRegular boxSize="16px" />
              </StepIconButton>
              <StepIconButton
                danger
                label={t('common:action.delete')}
                onClick={() => {
                  remove(subIndex)
                }}
              >
                <FaXmarkRegular boxSize="16px" />
              </StepIconButton>
            </HStack>

            {/* row1.5: 名称のエラー文言 (2026-08-18 デザイナー指摘)。
                **row1 の外**に置くのが肝。 入力欄の直下 (FieldRoot の中) に置くと
                名称欄だけ背が高くなり、 `align-items:center` の行では エラーと無関係な
                リンク先 / 担当者 / ボタン が再センタリングされて約 9px 下へ動く
                (Chromium 実測)。 行外なら行の高さは変わらないので、 エラーの有無で
                入力欄の位置が動かない。
                インデントは row2 (説明) と同じ 32px = 番号バッジ 24px + gap 8px で、
                名称入力欄の左端に揃う。 */}
            {subErrors?.name?.message !== undefined && (
              <Text
                paddingLeft="32px"
                color="Text.negativeHigh"
                fontSize="sm"
                id={`subStep-name-error-${bigStepIndex}-${subIndex}`}
              >
                {subErrors.name.message}
              </Text>
            )}

            {subErrors?.linkedPageKind?.message !== undefined && (
              <Text
                paddingLeft="32px"
                color="Text.negativeHigh"
                fontSize="sm"
                role="alert"
              >
                {subErrors.linkedPageKind.message}
              </Text>
            )}

            {subErrors?.approvalSetting?.message !== undefined && (
              <Text
                paddingLeft="32px"
                color="Text.negativeHigh"
                fontSize="sm"
                id={approvalErrorId}
              >
                {subErrors.approvalSetting.message}
              </Text>
            )}

            {/* row2: 説明 (インデント 32px・全幅・maxLength=100) */}
            <Box paddingLeft="32px">
              <FieldRoot
                alignItems="stretch"
                flex="1 1 0"
                minWidth={0}
                invalid={subErrors?.description !== undefined}
              >
                <Controller
                  control={control}
                  name={`bigSteps.${bigStepIndex}.subSteps.${subIndex}.description`}
                  render={({ field: f }) => (
                    <Input
                      {...f}
                      width="100%"
                      aria-label={t(
                        'page:adminWorkflowTemplates.subStep.description',
                      )}
                      id={`subStep-description-${bigStepIndex}-${subIndex}`}
                      onChange={(e) => {
                        f.onChange(
                          truncateToCodePointLength(e.target.value, 100),
                        )
                      }}
                      placeholder={t(
                        'page:adminWorkflowTemplates.subStep.descriptionPlaceholder',
                      )}
                      size="md"
                      value={f.value ?? ''}
                    />
                  )}
                />
                <FieldErrorText>
                  {subErrors?.description?.message}
                </FieldErrorText>
              </FieldRoot>
            </Box>
            {allowedApprovalModes(subSteps?.[subIndex]?.linkedPageKind).length >
              0 && (
              <SubStepApprovalSettingField
                bigStepIndex={bigStepIndex}
                control={control}
                errorId={undefined}
                linkedPageKind={subSteps?.[subIndex]?.linkedPageKind}
                members={members}
                methodsOnly
                modes={allowedApprovalModes(
                  subSteps?.[subIndex]?.linkedPageKind,
                )}
                subIndex={subIndex}
              />
            )}
            <SubStepPlanCreationTimeField
              automationAvailable={automationAvailable}
              bigStepIndex={bigStepIndex}
              control={control}
              subIndex={subIndex}
            />
            <SubStepPlanAutoSubmitOffsetField
              automationAvailable={automationAvailable}
              bigStepIndex={bigStepIndex}
              control={control}
              subIndex={subIndex}
            />
          </VStack>
        )
      })}

      {typeof bigStepErrors === 'string' && (
        <Text color="Text.negativeMid" fontSize="sm">
          {bigStepErrors}
        </Text>
      )}

      {/* サブステップ追加 (design .ct-add-sub: btn-secondary, 全幅) */}
      <SecondaryButton width="100%" onClick={handleAddSubStep}>
        <FaPlusRegular />
        {t('page:adminWorkflowTemplates.action.addSubStep')}
      </SecondaryButton>
    </VStack>
  )
}
